import { useEffect, useMemo, useState } from 'react';

import { api, ApiError } from '../lib/api';
import type { PricedCart } from '../lib/api';
import { money } from '../lib/i18n';
import { useSeo } from '../lib/head';
import { useNavigate } from '../lib/router';
import { privateMeta } from '../lib/seo';
import { useStore } from '../lib/store';

type Delivery = 'PICKUP' | 'YEREVAN_COURIER' | 'ARMENIA_POST' | 'INTERNATIONAL';
type Payment = 'TRANSFER' | 'CRYPTO' | 'CASH';

export function Checkout() {
  const { lang, currency, t, lines, clear } = useStore();
  useSeo(privateMeta(lang, 'checkoutTitle'));
  const navigate = useNavigate();

  const [delivery, setDelivery] = useState<Delivery>('YEREVAN_COURIER');
  const [payment, setPayment] = useState<Payment>('TRANSFER');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [telegram, setTelegram] = useState('');
  const [address, setAddress] = useState('');
  const [comment, setComment] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [cart, setCart] = useState<PricedCart | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Ключ идемпотентности. Генерируется один раз на визит: если связь оборвётся
   * и покупатель нажмёт «отправить» второй раз, сервер вернёт тот же заказ,
   * а не создаст второй.
   */
  const idempotencyKey = useMemo(
    () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())),
    [],
  );

  useEffect(() => {
    if (lines.length === 0) {
      navigate('/cart', { replace: true });
      return;
    }

    let cancelled = false;
    api
      .validateCart(lang, { items: lines, currency, deliveryMethod: delivery })
      .then((c) => {
        if (!cancelled) setCart(c);
      })
      .catch(() => {
        if (!cancelled) setError(t.error);
      });

    return () => {
      cancelled = true;
    };
  }, [lang, currency, lines, delivery, navigate, t.error]);

  const needsAddress = delivery !== 'PICKUP';

  // Наличные — только в Ереване. Сменили доставку на почту или заграницу —
  // снимаем выбор наличных, чтобы не отправить заказ, который сервер отклонит
  const cashAllowed = delivery === 'YEREVAN_COURIER' || delivery === 'PICKUP';
  useEffect(() => {
    if (!cashAllowed && payment === 'CASH') setPayment('TRANSFER');
  }, [cashAllowed, payment]);

  /**
   * Проверка полей при отправке, а не заблокированная кнопка.
   *
   * Серая кнопка без объяснений выглядит для покупателя как «сайт сломан»:
   * он не знает, что именно не так. Поэтому кнопка нажимается всегда, а под
   * каждым неверным полем появляется, что поправить.
   */
  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    if (name.trim().length < 2) errors.name = t.errName;
    if (phone.replace(/\D/g, '').length < 8) errors.phone = t.errPhone;
    if (needsAddress && address.trim().length < 5) errors.address = t.errAddress;
    return errors;
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (sending) return;

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      document.getElementById(Object.keys(errors)[0])?.focus();
      return;
    }
    if (cart && !cart.minOrder.met) return;

    setSending(true);
    setError(null);

    try {
      const order = await api.createOrder(
        lang,
        {
          items: lines,
          currency,
          deliveryMethod: delivery,
          paymentMethod: payment,
          contact: {
            name: name.trim(),
            phone: phone.trim(),
            ...(telegram.trim() ? { telegram: telegram.trim() } : {}),
          },
          ...(needsAddress ? { address: { line1: address.trim() } } : {}),
          ...(comment.trim() ? { customerComment: comment.trim() } : {}),
          channel: 'WEB',
        },
        idempotencyKey,
      );

      clear();
      // Странице «заказ принят» нужно знать, что доставку ещё посчитаем,
      // иначе итог без доставки выглядит окончательным
      navigate(`/order/${order.number}`, {
        state: { ...order, deliveryQuotedSeparately: cart?.deliveryQuotedSeparately ?? false },
        replace: true,
      });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t.error);
      setSending(false);
    }
  }

  const deliveryOptions: Array<{ value: Delivery; label: string; hint?: string }> = [
    { value: 'YEREVAN_COURIER', label: t.yerevan, hint: money(1000, 'AMD', lang) },
    { value: 'PICKUP', label: t.pickup, hint: t.free },
    { value: 'ARMENIA_POST', label: t.armenia, hint: t.quotedSeparately },
    { value: 'INTERNATIONAL', label: t.international, hint: t.quotedSeparately },
  ];

  return (
    <div className="wrap page" style={{ maxWidth: '44rem' }}>
      <h1>{t.checkout}</h1>

      {error && <div className="notice notice--warn">{error}</div>}

      <form onSubmit={submit}>
        <div className="panel" style={{ marginBottom: '1.5rem' }}>
          <h2>{t.contacts}</h2>

          <div className="field">
            <label htmlFor="name">{t.name}</label>
            <input
              id="name"
              value={name}
              aria-invalid={Boolean(fieldErrors.name)}
              onChange={(e) => {
                setName(e.target.value);
                setFieldErrors(({ name: _, ...rest }) => rest);
              }}
            />
            {fieldErrors.name && <small className="field-error">{fieldErrors.name}</small>}
          </div>

          <div className="field">
            <label htmlFor="phone">{t.phone}</label>
            <input
              id="phone"
              type="tel"
              inputMode="tel"
              placeholder="+374 …"
              value={phone}
              aria-invalid={Boolean(fieldErrors.phone)}
              onChange={(e) => {
                setPhone(e.target.value);
                setFieldErrors(({ phone: _, ...rest }) => rest);
              }}
            />
            {fieldErrors.phone && <small className="field-error">{fieldErrors.phone}</small>}
          </div>

          <div className="field">
            <label htmlFor="telegram">{t.telegram}</label>
            <input
              id="telegram"
              placeholder="@username"
              autoCapitalize="off"
              autoCorrect="off"
              value={telegram}
              onChange={(e) => setTelegram(e.target.value)}
            />
            <small>{t.telegramHint}</small>
          </div>
        </div>

        <div className="panel" style={{ marginBottom: '1.5rem' }}>
          <h2>{t.deliveryMethod}</h2>

          <div className="radio-group">
            {deliveryOptions.map((option) => (
              <label key={option.value} className="radio">
                <input
                  type="radio"
                  name="delivery"
                  checked={delivery === option.value}
                  onChange={() => setDelivery(option.value)}
                />
                <span>
                  <b>{option.label}</b>
                  {option.hint && <small>{option.hint}</small>}
                </span>
              </label>
            ))}
          </div>

          {needsAddress && (
            <div className="field">
              <label htmlFor="address">{t.address}</label>
              <input
                id="address"
                value={address}
                aria-invalid={Boolean(fieldErrors.address)}
                onChange={(e) => {
                  setAddress(e.target.value);
                  setFieldErrors(({ address: _, ...rest }) => rest);
                }}
                placeholder={t.addressHint}
              />
              {fieldErrors.address && <small className="field-error">{fieldErrors.address}</small>}
            </div>
          )}

          <div className="field">
            <label htmlFor="comment">{t.comment}</label>
            <textarea
              id="comment"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        </div>

        <div className="panel" style={{ marginBottom: '1.5rem' }}>
          <h2>{t.payment}</h2>
          <div className="radio-group">
            <label className="radio">
              <input
                type="radio"
                name="payment"
                checked={payment === 'TRANSFER'}
                onChange={() => setPayment('TRANSFER')}
              />
              <span>
                <b>{t.transfer}</b>
                <small>
                  {t.transferHint}
                </small>
              </span>
            </label>
            <label className="radio">
              <input
                type="radio"
                name="payment"
                checked={payment === 'CRYPTO'}
                onChange={() => setPayment('CRYPTO')}
              />
              <span>
                <b>{t.crypto}</b>
                <small>{t.cryptoHint}</small>
              </span>
            </label>
            {cashAllowed && (
              <label className="radio">
                <input
                  type="radio"
                  name="payment"
                  checked={payment === 'CASH'}
                  onChange={() => setPayment('CASH')}
                />
                <span>
                  <b>{t.cash}</b>
                </span>
              </label>
            )}
          </div>
        </div>

        {cart && (
          <div className="panel">
            <div className="totals" style={{ marginTop: 0, paddingTop: 0, borderTop: 0 }}>
              <div>
                <span className="muted">{t.subtotal}</span>
                <span>{money(cart.subtotal, currency, lang)}</span>
              </div>
              <div>
                <span className="muted">{t.delivery}</span>
                <span>
                  {cart.deliveryQuotedSeparately
                    ? t.quotedSeparately
                    : money(cart.delivery, currency, lang)}
                </span>
              </div>
              <div className="grand">
                <span>{t.total}</span>
                <span>
                  {money(cart.total, currency, lang)}
                  {cart.deliveryQuotedSeparately && `\u00a0${t.plusDelivery}`}
                </span>
              </div>
            </div>
          </div>
        )}

        {cart && !cart.minOrder.met && cart.minOrder.required !== null && (
          <div className="notice notice--warn" style={{ marginTop: '1.5rem' }}>
            {t.minOrderLeft(
              money(cart.minOrder.required - (cart.subtotal - cart.discount), currency, lang),
            )}
          </div>
        )}

        {Object.keys(fieldErrors).length > 0 && (
          <div className="notice notice--warn" style={{ marginTop: '1.5rem' }}>
            {t.checkFields}
          </div>
        )}

        <button
          type="submit"
          className="btn btn--block"
          style={{ marginTop: '1.5rem' }}
          disabled={sending || (cart !== null && !cart.minOrder.met)}
        >
          {sending ? t.sending : t.placeOrder}
        </button>
      </form>
    </div>
  );
}
